import React from 'react';
import { Link } from 'react-router-dom';
import '../CSS/PrivatePolicy.css';

const PrivatePolicy: React.FC = () => {
  return (
    <div className="private-policy-page">
      <div className="private-policy-hero-banner">
        <div className="private-policy-hero-content">
          <h1 className="private-policy-title">Privacy Policy</h1>
          <p className="private-policy-subtitle">Last Updated: January 2025</p>
        </div>
      </div>
      
      <div className="private-policy-body">
        <div className="policy-intro">
          <h1 className="policy-main-title">Myers McRae Privacy Policy</h1>
          <p className="policy-website">
            Website: <Link to="/" className="policy-link">www.myersmcrae.com</Link>
          </p>
          <p className="policy-updated">Last Updated: October 2025</p>
        </div>

        <div className="policy-introduction">
          <h2 className="policy-section-title">Introduction</h2>
          <div className="policy-section-content">
            <p>
              At Myers McRae, we value your privacy and are committed to safeguarding your personal information. This Privacy Policy explains how we collect, use, share, and protect the personal data you provide when using our website, <Link to="/" className="policy-link">www.myersmcrae.com</Link> ("Website," "Service").
            </p>
            <p>
              By using our website, you agree to the practices described in this Policy. If you do not agree, please do not use our website.
            </p>
          </div>
        </div>

        <div className="policy-scope">
          <h2 className="policy-section-title">Scope and Applicability</h2>
          <div className="policy-section-content">
            <p>
              This Privacy Policy applies to all visitors, users, and others who access our Website regardless of their geographic location.
            </p>
          </div>
        </div>

        <div className="policy-information-collect">
          <h2 className="policy-section-title">Information We Collect</h2>
          <div className="policy-section-content">
            <p>
              We collect information from and about you in the following ways:
            </p>
            
            <h3 className="policy-subsection-title">a. Information You Provide Voluntarily</h3>
            <p>
              When you contact us or submit your credentials as a candidate, you may provide:
            </p>
            <ul className="policy-list">
              <li>Name, address, phone number, and email address</li>
              <li>Resume and professional background (employment history, education, credentials, affiliations, references, etc.)</li>
              <li>Any additional information you choose to share with us, which may include Sensitive Personal Information (such as health data or union membership), which we process only when necessary for the role and with your explicit permission.</li>
            </ul>

            <h3 className="policy-subsection-title">b. Information Collected Automatically</h3>
            <p>
              When you visit our website, we may automatically collect:
            </p>
            <ul className="policy-list">
              <li>IP address, browser type, operating system, and device identifiers</li>
              <li>Date, time, and duration of visits</li>
              <li>Pages visited, referring/exit pages, and usage statistics</li>
              <li>Cookie and analytics data (see Section 7)</li>
            </ul>

            <h3 className="policy-subsection-title">c. Information from Third Parties</h3>
            <p>
              We may receive limited information from third-party services (such as analytics, recruitment tools, or background check providers) to support our services.
            </p>
          </div>
        </div>

        <div className="policy-information-use">
          <h2 className="policy-section-title">How We Use Your Information</h2>
          <div className="policy-section-content">
            <p>
              We use your information for the following purposes:
            </p>
            <ul className="policy-list">
              <li>Assess qualifications and match candidates with client opportunities</li>
              <li>Communicate with you regarding career opportunities or inquiries</li>
              <li>Improve our website, services, and user experience</li>
              <li>Maintain records for business or compliance purposes</li>
              <li>Meet legal and regulatory obligations</li>
            </ul>
            <p>
              We do not sell personal information for monetary gain.
            </p>
          </div>
        </div>

        <div className="policy-data-sharing">
          <h2 className="policy-section-title">Data Sharing and Disclosure</h2>
          <div className="policy-section-content">
            <p>
              We may share personal data only as necessary and as permitted by law:
            </p>
            <ul className="policy-list">
              <li>With service providers or contractors who perform services on our behalf (e.g., background checks, analytics, hosting).</li>
              <li>With clients seeking candidates for positions, you apply or are qualified for.</li>
              <li>When required by law, regulation, subpoena, or other legal process.</li>
              <li>During a business transfer such as a merger, acquisition, or asset sale.</li>
            </ul>
            <p>
              All service providers are bound by confidentiality and data-protection agreements.
            </p>
          </div>
        </div>

        <div className="policy-data-retention">
          <h2 className="policy-section-title">Data Retention</h2>
          <div className="policy-section-content">
            <p>
              We retain personal information only as long as necessary for legitimate business or legal purposes, including:
            </p>
            <ul className="policy-list">
              <li>To fulfill the purposes for which it was collected.</li>
              <li>To comply with applicable laws and regulations.</li>
              <li>To resolve disputes and enforce agreements.</li>
            </ul>
            <p>
              When no longer needed, information will be securely deleted or anonymized.
            </p>
          </div>
        </div>

        <div className="policy-cookies">
          <h2 className="policy-section-title">Cookies and Analytics</h2>
          <div className="policy-section-content">
            <p>
              We use cookies and similar technologies to enhance your experience and analyze website traffic. Cookies help us understand which pages are most useful to our visitors. You can disable cookies in your browser settings, though some features may not function properly.
            </p>
            <p>
              We may use third-party analytics tools (e.g., <strong>Google Analytics</strong>) which collect anonymized usage information. These providers have their own privacy policies.
            </p>
          </div>
        </div>

        <div className="policy-data-security">
          <h2 className="policy-section-title">Data Security</h2>
          <div className="policy-section-content">
            <p>
              We employ commercially reasonable technical and organizational safeguards to protect personal data from unauthorized access, loss, misuse, or alteration. However, no method of transmission or electronic storage is completely secure, and we cannot guarantee absolute security.
            </p>
            <p>
              In the event of a data breach involving personal data, we will notify affected individuals and regulatory authorities as required by law.
            </p>
          </div>
        </div>

        <div className="policy-privacy-change">
          <h2 className="policy-section-title">Changes to This Privacy Policy</h2>
          <div className="policy-section-content">
            <p>
              We may update this Privacy Policy periodically. Updates will be posted on this page with a new "Last Updated" date. For material changes, we will provide additional notice (such as a website banner or email) before the changes take effect.
            </p>
            <p>
              Continued use of the website after such notice constitutes acceptance of the updated policy.
            </p>
          </div>
        </div>

        <div className="policy-contact-us">
          <h2 className="policy-section-title">Contact Us</h2>
          <div className="policy-section-content">
            <p>
              If you have questions, concerns, or requests related to this Privacy Policy, please contact us:
            </p>
            <p className="policy-contact-info">
              Email: <a href="mailto:myersmcrae@myersmcrae.com" className="policy-link">myersmcrae@myersmcrae.com</a>
            </p>
            <p className="policy-contact-info">
              Myers McRae | Executive Search and Consulting
            </p>
            <p className="policy-contact-info">
              <Link to="/" className="policy-link">www.myersmcrae.com</Link>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PrivatePolicy;

